import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, AlertTriangle, TrendingUp, Layers, Target } from "lucide-react";
import { useAssessment } from "../context/AssessmentContext";
import { PageShell } from "../components/PageShell";
import { TopProgressBar } from "../components/TopProgressBar";
import { analyzeAnswers } from "../lib/scopeEngine";

export default function Results() {
  const navigate = useNavigate();
  const { answers, client } = useAssessment();
  const analysis = useMemo(() => analyzeAnswers(answers, client.stage), [answers, client.stage]);

  return (
    <PageShell progressBar={<TopProgressBar value={94} />} max="max-w-7xl">
      <div data-testid="results-page">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#1FA8E0] mb-4">
            Diagnostic Complete · Summary
          </div>
          <div className="flex items-start justify-between flex-wrap gap-6">
            <h1 className="font-display font-bold text-4xl md:text-5xl text-[#0A1A2F] leading-tight tracking-tight max-w-2xl">
              Here's how things look for
              <span className="block text-[#1FA8E0]">{client.business_name || "your business"}.</span>
            </h1>
            <div className="flex items-center gap-3">
              <button
                data-testid="results-back-btn"
                onClick={() => navigate("/questionnaire")}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-sm border border-[#E5E7EB] text-[#6B7280] hover:text-[#0A1A2F] hover:border-[#0A1A2F] transition-colors text-sm"
              >
                <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
                Adjust answers
              </button>
              <button
                data-testid="view-scope-btn"
                onClick={() => navigate("/scope")}
                className="inline-flex items-center gap-3 bg-[#0A1A2F] hover:bg-[#2F5D8C] text-white px-7 py-3 rounded-sm transition-all duration-300"
              >
                <span className="text-xs tracking-[0.2em] uppercase">View Scope of Work</span>
                <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </motion.div>

        {/* Bento grid */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-6">
          {/* Risk gauge - large */}
          <Card className="md:col-span-5 lg:col-span-5 row-span-2" delay={0.05} testid="card-risk-score">
            <CardLabel icon={AlertTriangle} text="Business Risk Summary" />
            <div className="mt-6 flex items-end gap-4">
              <div className="font-display font-bold text-7xl md:text-8xl text-[#0A1A2F] leading-none tracking-tight">
                {analysis.riskScore}
              </div>
              <div className="pb-3">
                <div className="text-[11px] tracking-[0.22em] uppercase text-[#1FA8E0]">Risk Index</div>
                <div className="text-sm text-[#6B7280] mt-1">out of 100</div>
              </div>
            </div>
            <div className="mt-6">
              <div className="h-2 w-full bg-[#F5F7FA] rounded-full overflow-hidden">
                <motion.div
                  className="h-full"
                  style={{
                    background: analysis.riskScore >= 65 ? "#0A1A2F" : analysis.riskScore >= 35 ? "#2F5D8C" : "#1FA8E0",
                  }}
                  initial={{ width: 0 }}
                  animate={{ width: `${analysis.riskScore}%` }}
                  transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
                />
              </div>
              <div className="flex justify-between mt-2 text-[10px] tracking-[0.2em] uppercase text-[#9CA3AF]">
                <span>Low</span><span>Moderate</span><span>Elevated</span>
              </div>
            </div>
            <p className="mt-6 text-[#6B7280] text-sm leading-relaxed">
              {analysis.riskSummary}
            </p>
          </Card>

          {/* Complexity */}
          <Card className="md:col-span-3 lg:col-span-3" delay={0.1} testid="card-complexity">
            <CardLabel icon={Layers} text="Engagement Complexity" />
            <div className="mt-6 font-display font-bold text-2xl md:text-3xl text-[#0A1A2F] leading-tight break-words">
              {analysis.complexity}
            </div>
            <p className="mt-4 text-sm text-[#6B7280] leading-relaxed">
              Indicative depth of the recommended advisory engagement.
            </p>
          </Card>

          {/* Priority areas */}
          <Card className="md:col-span-4 lg:col-span-4" delay={0.15} testid="card-priority-areas">
            <CardLabel icon={Target} text="Priority Areas" />
            <ul className="mt-6 space-y-3">
              {analysis.priorityTags.length === 0 && (
                <li className="text-sm text-[#6B7280]">No immediate priorities flagged.</li>
              )}
              {analysis.priorityTags.map((p, i) => (
                <li key={p} className="flex items-center gap-3" data-testid={`priority-item-${i}`}>
                  <span className="font-display font-bold text-[#1FA8E0] text-lg w-6">0{i + 1}</span>
                  <span className="text-[#0A1A2F] text-sm">{p}</span>
                </li>
              ))}
            </ul>
          </Card>

          {/* Key challenges */}
          <Card className="md:col-span-4 lg:col-span-7" delay={0.2} testid="card-challenges">
            <CardLabel icon={AlertTriangle} text="Key Concerns Identified" />
            {analysis.challenges.length === 0 ? (
              <p className="mt-6 text-sm text-[#6B7280]">No specific concerns flagged from the diagnostic.</p>
            ) : (
              <ul className="mt-6 space-y-3">
                {analysis.challenges.map((c, i) => (
                  <li key={c} className="flex gap-4 pb-3 border-b border-[#E5E7EB] last:border-0 last:pb-0">
                    <span className="text-[#1FA8E0] text-xs tracking-[0.2em] uppercase pt-1">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-[#0A1A2F] text-sm leading-relaxed">{c}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* Recommended services */}
          <Card className="md:col-span-8 lg:col-span-5" delay={0.25} testid="card-services">
            <CardLabel icon={TrendingUp} text="Recommended Support Areas" />
            {analysis.recommendedServices.length === 0 ? (
              <p className="mt-6 text-sm text-[#6B7280]">A general discovery session is recommended.</p>
            ) : (
              <div className="mt-6 space-y-3 max-h-[320px] overflow-y-auto pr-1">
                {analysis.recommendedServices.map((s) => (
                  <div key={s.title} className="px-4 py-3 rounded-sm bg-[#F5F7FA] border border-[#E5E7EB]">
                    <div className="font-display font-bold text-[#0A1A2F] text-base leading-snug">{s.title}</div>
                    <div className="text-xs text-[#6B7280] mt-1 leading-relaxed">{s.description}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Suggested focus areas */}
          <Card className="md:col-span-8 lg:col-span-12" delay={0.3} testid="card-focus-areas">
            <CardLabel icon={Target} text="Suggested Focus Areas" />
            <div className="mt-6 flex flex-wrap gap-2">
              {analysis.focusAreas.length === 0 && (
                <span className="text-sm text-[#6B7280]">To be confirmed during discovery.</span>
              )}
              {analysis.focusAreas.map((f) => (
                <span
                  key={f}
                  className="px-4 py-2 rounded-sm border border-[#2F5D8C]/30 text-[#2F5D8C] text-sm bg-white"
                >
                  {f}
                </span>
              ))}
            </div>
          </Card>
        </div>

        <div className="mt-12 flex justify-end">
          <button
            data-testid="view-scope-btn-bottom"
            onClick={() => navigate("/scope")}
            className="inline-flex items-center gap-3 bg-[#0A1A2F] hover:bg-[#2F5D8C] text-white px-8 py-4 rounded-sm transition-all duration-300"
          >
            <span className="text-xs tracking-[0.2em] uppercase">Generate Scope of Work</span>
            <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </PageShell>
  );
}

const Card = ({ children, className = "", delay = 0, testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
    whileHover={{ y: -4 }}
    data-testid={testid}
    className={`bg-white rounded-lg border border-[#E5E7EB] elevated-card p-7 ${className}`}
  >
    {children}
  </motion.div>
);

const CardLabel = ({ icon: Icon, text }) => (
  <div className="flex items-center gap-2 text-[#1FA8E0]">
    <Icon className="h-4 w-4" strokeWidth={1.4} />
    <span className="text-[11px] tracking-[0.25em] uppercase font-medium">{text}</span>
  </div>
);
